import type { QuestionFilters } from '../types';

const STORAGE_KEY = 'qm.questions.lastFilter';

/**
 * Returns true when at least one filter field carries a non-empty value.
 * Single source of truth for "is a filter active?" used by the URL-seed gate
 * (only seed when no filter active).
 */
export function isFilterActive(filters: QuestionFilters): boolean {
  return Boolean(
    filters.curriculumId ||
      filters.versionId ||
      filters.classId ||
      filters.subjectId ||
      filters.chapterId ||
      filters.topicId ||
      filters.subtopicId ||
      filters.category ||
      (filters.types?.length ?? 0) > 0 ||
      filters.hardness ||
      filters.importance ||
      filters.standardId ||
      filters.standard,
  );
}

/** The pool is only fetched once the scope reaches a subject; wider scopes are too large to browse. */
export function hasSubjectScope(filters: QuestionFilters): boolean {
  return Boolean(filters.subjectId);
}

/**
 * Where a bare /questions visit should redirect to: the stored filter when the URL
 * has none; the stored taxonomy plus the URL's other facets when the URL lacks a
 * subject scope the store has; otherwise null (stay).
 */
export function seedFromStored(
  search: QuestionFilters,
  stored: QuestionFilters | null,
): QuestionFilters | null {
  if (!stored) return null;
  if (!isFilterActive(search)) return isFilterActive(stored) ? stored : null;
  if (hasSubjectScope(search) || !hasSubjectScope(stored)) return null;
  const { curriculumId, versionId, classId, subjectId, chapterId, topicId, subtopicId } = stored;
  const { category, types, hardness, importance, standard, standardId } = search;
  return {
    curriculumId,
    versionId,
    classId,
    subjectId,
    chapterId,
    topicId,
    subtopicId,
    category,
    types,
    hardness,
    importance,
    standard,
    standardId,
  };
}

/** The subject scope alone — taxonomy down to the subject, every narrower filter dropped. */
export function subjectScopeOf(filters: QuestionFilters): QuestionFilters {
  const { curriculumId, versionId, classId, subjectId } = filters;
  return { curriculumId, versionId, classId, subjectId };
}

/** Persist the current filter scope to localStorage. Called on every filter change and on save. */
export function saveLastFilter(filters: QuestionFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // localStorage may be unavailable in some environments; silently ignore.
  }
}

/** Clear the stored last-used filter (called when the curator resets all filters). */
export function clearLastFilter(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Load the stored last-used filter. Returns null when nothing is stored or JSON is invalid. */
export function loadLastFilter(): QuestionFilters | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    return parsed as QuestionFilters;
  } catch {
    return null;
  }
}
